import { useState } from "react"
import { useSelector } from "react-redux"
import { Link, Navigate } from "react-router-dom"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Alert, AlertDescription } from "../components/ui/alert"
import { useAuth } from "../hooks/use-auth"
import { errorMessage } from "../utils/mutationResult"
import { Eye, EyeOff, Loader2, Shield, WifiOff } from "lucide-react"

export function LoginForm() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)

  const { login, isLoading, error, clearError } = useAuth()
  // Signing in always requires a live request to the server, so we detect
  // offline up front and stop the user from submitting into a doomed
  // request instead of letting them wait on a spinner that can never finish.
  const isOnline = useSelector((state) => state.offline?.isOnline ?? true)
  const { isAuthenticated, user } = useSelector((state) => state.auth || { isAuthenticated: false, user: null })

  // A user who has previously logged in has a cached session (see
  // RouteGuard), which is trusted for offline access. Without this check,
  // reopening the PWA offline always lands on "/" and shows this form —
  // even for a returning user who should go straight to their dashboard.
  if (isAuthenticated && user) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isOnline) return
    // clearError()

    try {
      await login({ email, password })
    } catch (err) {
      // Error is handled by Redux state
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:bg-none dark:bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2 dark:text-foreground">EncoAudit</h1>
          <p className="text-slate-600 dark:text-muted-foreground">Sign in to access your audit dashboard</p>
        </div>

        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-blue-600 dark:text-link" />
            </div>
            <CardTitle className="text-xl font-semibold text-slate-900 dark:text-foreground">Welcome Back To EncoAudit</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-foreground">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700 dark:text-foreground">
                    Password
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 dark:text-link hover:text-blue-700 hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-0 top-0 flex h-11 w-10 items-center justify-center text-slate-500 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:text-muted-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {!isOnline && (
                <Alert variant="destructive">
                  <WifiOff className="h-4 w-4" />
                  <AlertDescription>
                    You&apos;re offline and haven&apos;t signed in on this device before. Connect to the internet
                    once to sign in — after that, you&apos;ll be able to work offline.
                  </AlertDescription>
                </Alert>
              )}

              {isOnline && error && (
                <Alert variant="destructive">
                  <AlertDescription>{errorMessage(error, "Unable to sign in. Please try again.")}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full h-11 bg-blue-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border hover:bg-blue-700 text-white font-medium"
                disabled={isLoading || !isOnline}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : !isOnline ? (
                  <>
                    <WifiOff className="w-4 h-4 mr-2" />
                    Offline
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default LoginForm
